import { useState, useEffect } from 'react';
import useFileDownload from './useFileDownload';
import { decryptMediaBlob } from '../utils/encryptionUtils';
import logger from '../utils/logger';

const thumbnailBlobUrls = new WeakMap();

/**
 * Hook to load a media thumbnail from a Matrix content URL (mxc://).
 * Handles both unencrypted and encrypted thumbnails with automatic decryption.
 *
 * @param {object} message - The Matrix event object.
 * @param {string|null} thumbnailMxcUrl - The mxc:// URL of the thumbnail (thumbnail_url or thumbnail_file.url).
 * @param {string|null} thumbnailMimeType - Optional MIME type for the thumbnail.
 * @param {string|null} fallbackMxcUrl - Optional fallback to main content URL if no thumbnail exists.
 * @param {string|null} fallbackMimeType - MIME type of the fallback content.
 * @param {object|null} encryptionInfo - Encryption info (key, iv, hashes) from thumbnail_file if encrypted.
 * @returns {{thumbnailUrl: string|null, isLoading: boolean, error: string|null}}
 */
export const useMediaThumbnail = (
  message, 
  thumbnailMxcUrl, 
  thumbnailMimeType = null, 
  fallbackMxcUrl = null, 
  fallbackMimeType = null,
  encryptionInfo = null,
) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { downloadFileToDisplay } = useFileDownload();

  // Extract stable values from message
  const messageId = message?.getId();
  const roomId = message?.getRoomId();
  
  // Determine which URL and mimetype to use
  // Prefer thumbnail, but fall back to main content for image/video types
  const mxcUrl = thumbnailMxcUrl || fallbackMxcUrl;
  const mimeType = thumbnailMxcUrl ? thumbnailMimeType : fallbackMimeType;

  useEffect(() => {
    logger.debug(`useMediaThumbnail: Loading thumbnail for mxcUrl=${mxcUrl}, encrypted=${!!encryptionInfo}, messageId=${messageId}`);
    let isMounted = true;
    const blobUrlRef = { current: null };

    const loadThumbnail = async () => {
      if (!mxcUrl) {
        setThumbnailUrl(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Download the file (encrypted or unencrypted bytes)
        const url = await downloadFileToDisplay({
          url: mxcUrl,
          roomId,
          mimeType: mimeType || 'image/png',
          skipCache: !!encryptionInfo,
        });
        
        let finalBlobUrl = url;

        // If encrypted, decrypt the downloaded blob
        if (encryptionInfo) {
          const { decryptedBlobUrl } = await decryptMediaBlob(url, encryptionInfo, mimeType || 'image/png');
          finalBlobUrl = decryptedBlobUrl;
        }

        blobUrlRef.current = finalBlobUrl; // Store in ref for cleanup access

        if (isMounted) {
          setThumbnailUrl(finalBlobUrl);
          if (message) {
            thumbnailBlobUrls.set(message, finalBlobUrl);
          }
        } else {
          // Component unmounted during fetch - revoke immediately
          URL.revokeObjectURL(finalBlobUrl);
        }
      } catch (e) {
        logger.error('Failed to load thumbnail:', e);
        if (isMounted) {
          setError('Failed to load thumbnail');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      isMounted = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        // Remove from WeakMap to prevent other components from retrieving revoked URL
        if (message) {
          thumbnailBlobUrls.delete(message);
        }
      }
    };
  }, [mxcUrl, downloadFileToDisplay, messageId, roomId, mimeType, message, encryptionInfo]);

  return { thumbnailUrl, isLoading, error };
};

export const getThumbnailBlobUrl = (message) => {
  return thumbnailBlobUrls.get(message);
};